import React, { useState, useEffect } from "react";
import axios from "axios";
import { Trash } from "lucide-react";

const BannerAdmin = () => {
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Default typing words matching current banner
  const defaultTypingWords = [
    "SEO-Friendly Titles",
    "Engaging Descriptions",
    "Feature Highlights",
    "Persuasive Bullet Points",
    "Product Tags",
    "Meta Titles",
    "Meta Descriptions",
  ];

  // Fetch banner data
  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        const response = await axios.get("https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/banner-section");
        if (response.data.success) {
          setBannerData(response.data.data);
        } else {
          setError("Failed to fetch banner data");
        }
      } catch (err) {
        setError("Error fetching banner data");
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBannerData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBannerData({
      ...bannerData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle typing words changes
  const handleTypingWordsChange = (e, index) => {
    const newWords = [...(bannerData.typingWords || defaultTypingWords)];
    newWords[index] = e.target.value;
    setBannerData({
      ...bannerData,
      typingWords: newWords,
    });
  };

  // Add a new typing word
  const addTypingWord = () => {
    setBannerData({
      ...bannerData,
      typingWords: [...(bannerData.typingWords || defaultTypingWords), ""],
    });
  };

  // Remove a typing word
  const removeTypingWord = (index) => {
    if ((bannerData.typingWords || defaultTypingWords).length <= 1) return;
    const newWords = [...(bannerData.typingWords || defaultTypingWords)];
    newWords.splice(index, 1);
    setBannerData({
      ...bannerData,
      typingWords: newWords,
    });
  };

  // Save banner data
  const saveBannerData = async () => {
    setSaving(true);
    setSuccess(false);
    setError(null);
    setDebugInfo(null);
    
    // Prepare the data to be sent
    const dataToSend = {
      ...bannerData,
      typingWords: bannerData.typingWords || defaultTypingWords
    };
    
    // Log the data being sent for debugging
    console.log("Sending data to server:", dataToSend);
    
    try {
      const response = await axios.put("https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/banner-section", dataToSend);
      console.log("Server response:", response.data);
      
      if (response.data.success) {
        setSuccess(true);
        // Update local data with response
        setBannerData(response.data.data);
      } else {
        setError(response.data.message || "Failed to save banner data");
      }
    } catch (err) {
      console.error("API Error:", err);
      
      // Enhanced error handling
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
        setError(`Server error: ${err.response.status} - ${err.response.data.message || err.response.statusText}`);
        setDebugInfo(JSON.stringify(err.response.data, null, 2));
      } else if (err.request) {
        // The request was made but no response was received
        console.error("Request error:", err.request);
        setError("No response received from server. Please check your connection.");
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error:", err.message);
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  // Reset to default
  const resetToDefault = async () => {
    if (!window.confirm("Are you sure you want to reset to default settings?")) return;
    
    setSaving(true);
    setSuccess(false);
    setError(null);
    setDebugInfo(null);
    
    try {
      const response = await axios.post("https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/banner-section/reset");
      if (response.data.success) {
        setSuccess(true);
        setBannerData(response.data.data);
      } else {
        setError(response.data.message || "Failed to reset banner data");
      }
    } catch (err) {
      console.error("API Error:", err);
      
      // Enhanced error handling
      if (err.response) {
        setError(`Server error: ${err.response.status} - ${err.response.data.message || err.response.statusText}`);
        setDebugInfo(JSON.stringify(err.response.data, null, 2));
      } else if (err.request) {
        setError("No response received from server. Please check your connection.");
      } else {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{height: '200px'}}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <span className="ms-3 fs-5">Loading banner data...</span>
    </div>
  );
  
  if (!bannerData) return (
    <div className="alert alert-danger d-flex align-items-center" role="alert">
      <i className="bi bi-exclamation-triangle-fill me-2"></i>
      <div>No banner data found</div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-0 py-3">
              <div className="d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                  <i className="bi bi-megaphone-fill text-primary fs-4"></i>
                </div>
                <h2 className="mb-0 fw-bold text-dark">Banner Section Management</h2>
              </div>
            </div>
            
            <div className="card-body">
              {success && (
                <div className="alert alert-success d-flex align-items-center" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <div>Banner data saved successfully!</div>
                </div>
              )}
              
              {error && (
                <div className="alert alert-danger d-flex flex-column align-items-start" role="alert">
                  <div className="d-flex align-items-center w-100">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <div>{error}</div>
                  </div>
                  {debugInfo && (
                    <details className="mt-2 w-100">
                      <summary className="cursor-pointer">Debug Information</summary>
                      <pre className="mt-2 p-2 bg-light rounded text-dark small">{debugInfo}</pre>
                    </details>
                  )}
                </div>
              )}
              
              <div className="row g-4 mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark">
                    <i className="bi bi-text-paragraph me-1 text-primary"></i> Sub Title
                  </label>
                  <input
                    type="text"
                    name="subTitle"
                    value={bannerData.subTitle || ""}
                    onChange={handleInputChange}
                    className="form-control form-control-lg border-0 shadow-sm"
                    placeholder="Enter sub title"
                  />
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark">
                    <i className="bi bi-type-h1 me-1 text-primary"></i> Main Title
                  </label>
                  <input
                    type="text"
                    name="mainTitle"
                    value={bannerData.mainTitle || ""}
                    onChange={handleInputChange}
                    className="form-control form-control-lg border-0 shadow-sm"
                    placeholder="Enter main title"
                  />
                </div>
                
                
                
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark">
                    <i className="bi bi-cursor me-1 text-primary"></i> Button Text
                  </label>
                  <input
                    type="text"
                    name="buttonText"
                    value={bannerData.buttonText || ""}
                    onChange={handleInputChange}
                    className="form-control form-control-lg border-0 shadow-sm"
                    placeholder="Enter button text"
                  />
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark">
                    <i className="bi bi-link-45deg me-1 text-primary"></i> Button Link
                  </label>
                  <input
                    type="text"
                    name="buttonLink"
                    value={bannerData.buttonLink || ""}
                    onChange={handleInputChange}
                    className="form-control form-control-lg border-0 shadow-sm"
                    placeholder="Enter button link"
                  />
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark">
                    <i className="bi bi-youtube me-1 text-danger"></i> YouTube URL
                  </label>
                  <input
                    type="text"
                    name="youtubeUrl"
                    value={bannerData.youtubeUrl || ""}
                    onChange={handleInputChange}
                    className="form-control form-control-lg border-0 shadow-sm"
                    placeholder="Enter YouTube URL"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="form-label fw-semibold text-dark">
                  <i className="bi bi-image me-1 text-primary"></i> Background Image URL
                </label>
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    name="backgroundImage"
                    value={bannerData.backgroundImage || ""}
                    onChange={handleInputChange}
                    className="form-control border-0 shadow-sm"
                    placeholder="Enter background image URL"
                  />
                  {bannerData.backgroundImage && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => window.open(bannerData.backgroundImage, '_blank')}
                    >
                      <i className="bi bi-eye"></i> Preview
                    </button>
                  )}
                </div>
              </div>
              
              <div className="mb-4">
                <label className="form-label fw-semibold text-dark">
                  <i className="bi bi-card-text me-1 text-primary"></i> Description
                </label>
                <textarea
                  name="description"
                  value={bannerData.description || ""}
                  onChange={handleInputChange}
                  rows="4"
                  className="form-control border-0 shadow-sm"
                  placeholder="Enter description"
                ></textarea>
              </div>
              
              <div className="mb-4">
                <label className="form-label fw-semibold text-dark d-flex align-items-center">
                  <i className="bi bi-list-ul me-1 text-primary"></i> 
                  Typing Words
                  <span className="badge bg-primary rounded-pill ms-2">{(bannerData.typingWords || defaultTypingWords).length}</span>
                </label>
                
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-0">
                    {(bannerData.typingWords || defaultTypingWords).map((word, index) => (
                      <div key={index} className="d-flex align-items-center p-3 border-bottom">
                        <div className="input-group flex-grow-1">
                          <span className="input-group-text bg-light border-0">
                            <i className="bi bi-text-center text-muted"></i>
                          </span>
                          <input
                            type="text"
                            value={word}
                            onChange={(e) => handleTypingWordsChange(e, index)}
                            className="form-control border-0 shadow-sm"
                            placeholder={`Word ${index + 1}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTypingWord(index)}
                          className="btn btn-sm btn-outline-danger ms-2"
                          disabled={(bannerData.typingWords || defaultTypingWords).length <= 1}
                          title="Remove word"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="card-footer bg-white border-0 p-3">
                    <button
                      type="button"
                      onClick={addTypingWord}
                      className="btn btn-sm btn-outline-primary d-flex align-items-center"
                    >
                      <i className="bi bi-plus-circle me-1"></i> Add New Word
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="form-check form-switch form-check-lg">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={bannerData.isActive || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  <label htmlFor="isActive" className="form-check-label fw-semibold">
                    Banner is active
                  </label>
                </div>
              </div>
              
              <div className="d-flex flex-wrap gap-3 pt-3 border-top">
                <button
                  onClick={saveBannerData}
                  disabled={saving}
                  className="btn btn-primary px-4 py-2 d-flex align-items-center"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i> Save Changes
                    </>
                  )}
                </button>
                
                <button
                  onClick={resetToDefault}
                  disabled={saving}
                  className="btn btn-outline-secondary px-4 py-2 d-flex align-items-center"
                >
                  <i className="bi bi-arrow-clockwise me-2"></i> Reset to Default
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerAdmin;